import { apiClient } from "./apiClient";

/** Every entity type crm-gateway accepts on /sync/replay. */
export const ALL_HISTORICAL_SYNC_ENTITY_TYPES = [
  "BRANCH",
  "NEGOTIATOR",
  "LEAD",
  "CONTACT",
  "PROPERTY",
  "PROPERTY_IMAGE",
  "PROPERTY_DOCUMENT",
] as const;

export type HistoricalSyncEntityType = (typeof ALL_HISTORICAL_SYNC_ENTITY_TYPES)[number];

/**
 * Default (branch-scoped) run order — the CRMs that map a hierarchy replay
 * these four per branch.
 */
export const HISTORICAL_SYNC_ENTITY_TYPES: readonly HistoricalSyncEntityType[] = [
  "NEGOTIATOR",
  "LEAD",
  "CONTACT",
  "PROPERTY",
];

/** Reapit additionally exposes property media during historical replay. */
export const REAPIT_HISTORICAL_SYNC_ENTITY_TYPES: readonly HistoricalSyncEntityType[] = [
  ...HISTORICAL_SYNC_ENTITY_TYPES,
  "PROPERTY_IMAGE",
  "PROPERTY_DOCUMENT",
];

/**
 * Propco run order. Propco has no branch hierarchy to map, so the replay is
 * legal-entity-scoped: BRANCH comes first (it is what creates the branches),
 * and there are no leads to replay.
 */
export const PROPCO_HISTORICAL_SYNC_ENTITY_TYPES: readonly HistoricalSyncEntityType[] = [
  "BRANCH",
  "NEGOTIATOR",
  "CONTACT",
  "PROPERTY",
];

/** Which id a historical sync is keyed to — a branch, or a whole legal entity. */
export type HistoricalSyncScope = "BRANCH" | "LEGAL_ENTITY";

export interface ReplayRequest {
  /** Null for a legal-entity-scoped replay (Propco), where there is no branch. */
  branchId: string | null;
  /** Sent instead of {@code branchId} when the replay covers a legal entity. */
  legalEntityId?: string;
  entityType: HistoricalSyncEntityType;
}

/** Entity types /crmgateway/sync/{entity-type}/{id} accepts for a single-record resync. */
export type ResyncEntityType = "CONTACT" | "PROPERTY" | "APPOINTMENT";

/**
 * Atlas enum → the `{entity-type}` path segment. Not a lowercase of the enum:
 * APPOINTMENT is addressed as `diary-entry`, so the mapping stays explicit.
 */
const RESYNC_PATH_SEGMENT: Record<ResyncEntityType, string> = {
  CONTACT: "contact",
  PROPERTY: "property",
  APPOINTMENT: "diary-entry",
};

export interface ResyncRequest {
  /** Atlas id of the record to write back — not the CRM's `externalId`. */
  entityId: string;
  agencyId: string;
  crmType: string;
  entityType: ResyncEntityType;
}

/**
 * Shape of crm-gateway's /sync/replay response, propagated verbatim by
 * atlas-api. {@code status} drives the badge colour in the UI;
 * {@code jobsGenerated} is shown alongside it.
 */
export interface ReplayResponse {
  id?: string;
  agencyId?: string;
  entityType?: string;
  fromTs?: string;
  toTs?: string;
  status?: string;
  jobsGenerated?: number;
  splitJob?: boolean;
  createdAt?: string;
  new?: boolean;
  // Error-path fields when atlas-api wraps an upstream failure.
  code?: string;
  message?: string;
}

/**
 * Atlas-api resolves agencyId / crmType / from / to server-side from the
 * branch row (specifically branch.created_at — NOT atlas_created_at).
 * The UI only names the branch + which entity to replay.
 */
export const crmGatewaySyncService = {
  replay: (body: ReplayRequest) =>
    apiClient
      .post<ReplayResponse>("/crmgateway/sync-replay", body)
      .then((r) => r.data),
  /**
   * Writes a single record back to the CRM — the action behind the "Sync now"
   * button on list rows and detail drawers. Unlike {@link replay}, which is
   * branch-wide and historical, this names the agency and CRM explicitly.
   *
   * Entity type and id travel in the path; agencyId / crmType stay in the body.
   */
  resync: ({ entityType, entityId, ...body }: ResyncRequest) =>
    apiClient
      .post<ReplayResponse>(
        `/crmgateway/sync/${RESYNC_PATH_SEGMENT[entityType]}/${encodeURIComponent(entityId)}`,
        body,
      )
      .then((r) => r.data),
  /**
   * Latest historical-sync status for the given branches.
   *
   * Without {@code entityType}, behaves as before — aggregate "ready" across
   * all four entities. With {@code entityType}, the server filters to that
   * entity (e.g. NEGOTIATOR) and also returns a per-branch status map so the
   * UI can render which branches are still pending.
   */
  checkSyncReadiness: (params: {
    /** Branch ids, or legal entity ids when {@code scope} is LEGAL_ENTITY. */
    branchIds: string[];
    entityType?: HistoricalSyncEntityType;
    scope?: HistoricalSyncScope;
  }) => {
    // Propco (LEGAL_ENTITY scope) is already scoped by the `legalEntityId` the
    // apiClient injects on every GET — sending `legalEntityIds` as well put the
    // legal entity on the query twice. Set `legalEntityId` explicitly (so the
    // interceptor doesn't back-fill the header's entity instead) and send no
    // `legalEntityIds` at all.
    const key = params.scope === "LEGAL_ENTITY" ? "legalEntityId" : "branchIds";
    const query: Record<string, string> = { [key]: params.branchIds.join(",") };
    if (params.entityType) query.entityType = params.entityType;
    return apiClient
      .get<{ ready: boolean; branches?: Record<string, string> }>(
        "/crmgateway/sync-status",
        { params: query },
      )
      .then((r) => r.data);
  },
};

export const HISTORICAL_SYNC_ENTITY_LABELS: Record<HistoricalSyncEntityType, string> = {
  BRANCH: "Branches",
  NEGOTIATOR: "Negotiators",
  LEAD: "Leads",
  CONTACT: "Contacts",
  PROPERTY: "Properties",
  PROPERTY_IMAGE: "Property images",
  PROPERTY_DOCUMENT: "Property documents",
};

/** Statuses that mean a branch's historical sync is actively running. */
const ACTIVE_SYNC_STATUSES = new Set(["IN_PROGRESS", "RUNNING", "QUEUED"]);

export function isHistoricalSyncActive(status: string | null | undefined): boolean {
  return ACTIVE_SYNC_STATUSES.has(String(status ?? "").toUpperCase());
}

export function isHistoricalSyncComplete(status: string | null | undefined): boolean {
  const normalized = String(status ?? "").toUpperCase();
  return normalized === "COMPLETED" || normalized === "SUCCESS";
}

/** Per-branch → per-entity status map, e.g. `{ [branchId]: { NEGOTIATOR: "COMPLETED", … } }`. */
export type BranchEntityStatusMap = Record<string, Record<HistoricalSyncEntityType, string>>;

/**
 * The /sync-status endpoint requires an `entityType`, so status is fetched with
 * one call per entity (four in parallel). Returns a per-branch → per-entity
 * status map. A failed entity call defaults that entity to NOT_STARTED rather
 * than failing the whole lookup.
 */
export async function fetchBranchEntityStatuses(
  branchIds: string[],
  options: {
    /** Defaults to the branch-scoped four. */
    entityTypes?: readonly HistoricalSyncEntityType[];
    /** LEGAL_ENTITY sends the id as a single legalEntityId instead of branchIds. */
    scope?: HistoricalSyncScope;
  } = {},
): Promise<BranchEntityStatusMap> {
  const entityTypes = options.entityTypes ?? HISTORICAL_SYNC_ENTITY_TYPES;
  const scope = options.scope;
  const map = {} as BranchEntityStatusMap;
  for (const id of branchIds) {
    map[id] = {} as Record<HistoricalSyncEntityType, string>;
  }
  await Promise.all(
    entityTypes.map(async (entityType) => {
      try {
        const res = await crmGatewaySyncService.checkSyncReadiness({ branchIds, entityType, scope });
        for (const id of branchIds) {
          map[id][entityType] = String(res.branches?.[id] ?? "NOT_STARTED").toUpperCase();
        }
      } catch {
        for (const id of branchIds) {
          map[id][entityType] = map[id][entityType] ?? "NOT_STARTED";
        }
      }
    }),
  );
  return map;
}

/**
 * Collapses a branch's four per-entity statuses into a single aggregate status
 * used for the History icons and the modal's pre-run banner:
 * IN_PROGRESS (any entity active) > PAUSE (any paused) > COMPLETED (all done) >
 * NOT_STARTED (anything still pending).
 */
export function aggregateBranchStatus(
  entityStatuses: Partial<Record<HistoricalSyncEntityType, string>>,
  entityTypes: readonly HistoricalSyncEntityType[] = HISTORICAL_SYNC_ENTITY_TYPES,
): string {
  const values = entityTypes.map((e) =>
    String(entityStatuses[e] ?? "NOT_STARTED").toUpperCase(),
  );
  if (values.some((v) => ACTIVE_SYNC_STATUSES.has(v))) return "IN_PROGRESS";
  if (values.some((v) => v === "PAUSE" || v === "PAUSED")) return "PAUSE";
  if (values.every((v) => v === "COMPLETED" || v === "SUCCESS")) return "COMPLETED";
  return "NOT_STARTED";
}